import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Input, InputLabel, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPrompt, DialogPromptApi, DialogPromptMatchWordPicture } from "../../api/DialogPromptApi";
import { IVocabularyWord } from "../../api/VocabularyWordApi";
import AppBarContext from '../../context/AppBarContext';
import { useVocabularyWordListPinyin } from "../../hook/useVocabularyWordListPinyin";
import { arrayBufferToBase64 } from "../../util/base64Utils";
import { handleErrorMessage } from '../ErrorDialog';

interface DialogPromptMatchWordPictureFormProps {
    oldDialogPromptId: number;
    setNewDialogPrompt: React.Dispatch<React.SetStateAction<DialogPrompt | null>>;
    id: number;
    dialogId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    isHidden: boolean;
    yesResponse: string;
    noResponse: string;
}

/**
 * @deprecated Use DialogPromptMatchGameModel for new exercises. This form remains for editing legacy prompts.
 */
export const DialogPromptMatchWordPictureForm: React.FC<DialogPromptMatchWordPictureFormProps> = ({
    oldDialogPromptId, setNewDialogPrompt,
    id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, noResponse }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [imageError, setImageError] = React.useState<boolean[]>([]);
    const {
        vocabularyWordList,
        getVocabularyWordPinyinMap,
        getVocabularyWordErrorMap,
        setVocabularyWordValue,
        setVocabularyWordList,
        setVocabularyWordFields,
        clearVocabularyWordErrors,
        handleVocabularyWordTextChange,
        handleVocabularyWordPinyinChange,
    } = useVocabularyWordListPinyin({ history, location, setErrorMessage })

    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
    const [wordIndexToDelete, setWordIndexToDelete] = React.useState<number | null>(null);

    const setDialogPromptFields = React.useCallback((dialogPrompt: DialogPromptMatchWordPicture) => {
        if ('vocabulary_word_list' in dialogPrompt) {
            setVocabularyWordFields(dialogPrompt.vocabulary_word_list);
            // Create a new array with the same length as vocabulary_word_list
            const imageErrorArray = dialogPrompt.vocabulary_word_list.map((vocabularyWordOption) => {
                return (vocabularyWordOption.image == null || vocabularyWordOption.image == '') &&
                    (vocabularyWordOption.image_url == null || vocabularyWordOption.image_url == '');
            });

            // Set the imageError state with the new array
            setImageError(imageErrorArray);
        } else {
            setImageError([])
            setVocabularyWordFields([])
        }
        setDeleteConfirmationOpen(false)
        setWordIndexToDelete(null)

    }, [setVocabularyWordFields, setImageError, setDeleteConfirmationOpen, setWordIndexToDelete])

    React.useEffect(() => {
        DialogPromptApi.getById(oldDialogPromptId)
            .then((response) => {
                const oldDialogPrompt = response.data as DialogPromptMatchWordPicture;
                setDialogPromptFields(oldDialogPrompt);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        clearVocabularyWordErrors()
    }, [oldDialogPromptId, clearVocabularyWordErrors, history, location, setErrorMessage, setDialogPromptFields])



    React.useEffect(() => {
        const dialogPrompt = new DialogPromptMatchWordPicture(
            id,
            dialogId,
            orderIndex,
            isTeacher,
            prompt,
            isNoteToStudent,
            isHidden,
            yesResponse,
            noResponse,
            vocabularyWordList
        );
        setNewDialogPrompt(dialogPrompt);
    }, [id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, noResponse, vocabularyWordList, setNewDialogPrompt])

    const handleAddVocabularyWord = () => {
        const newVocabularyWord: IVocabularyWord = {
            id: 0,
            word: '',
            word_pinyin: [],
            image: '',
            image_url: '',
            audio_sentence: '',
            audio_sentence_pinyin: [],
            audio_sentence_id: 0,
            dialog_prompt_id: 0,
            teacher_english_prompt: '',
            teacher_chinese_prompt: '',
            teacher_chinese_prompt_pinyin: [],
            teacher_chinese_prompt_sentence_id: 0,
            student_response_prompt: '<Answer/>',
            student_response_prompt_pinyin: ['<Answer/>'],
            student_response_prompt_sentence_id: 0,
            word_info_text: null,
            english_text: null
        };
        setVocabularyWordList(prev => [...(Array.isArray(prev) ? prev : []), newVocabularyWord]);
    };

    const handleImageUpload = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        // Clear the image error for the specific image
        setImageError((prevImageError) => {
            const updatedError = [...prevImageError];
            updatedError[index] = false;
            return updatedError;
        });
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();

            reader.onloadend = () => {
                // Code to handle the reader result
                let base64String: string;
                const result = reader.result;
                if (typeof result === 'string') {
                    base64String = result;
                } else if (result instanceof ArrayBuffer) {
                    base64String = arrayBufferToBase64(result)
                } else {
                    base64String = ""
                }
                setVocabularyWordValue(index, 'image', base64String)
            };

            reader.readAsDataURL(file);
        }
    };
    const handleDeleteVocabularyWord = (index: number) => {
        setWordIndexToDelete(index);
        setDeleteConfirmationOpen(true);
    };

    const handleConfirmDelete = () => {
        if (wordIndexToDelete !== null) {
            setVocabularyWordList(prevList => prevList.filter((word, index) => index !== wordIndexToDelete));
        }
        setWordIndexToDelete(null);
        setDeleteConfirmationOpen(false);
    };

    const handleCancelDelete = () => {
        setWordIndexToDelete(null);
        setDeleteConfirmationOpen(false);
    };
    return (<>
        {vocabularyWordList && vocabularyWordList.map((word, index) => {
            // Assuming word.image contains the ArrayBuffer
            return (

                <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }} key={index}>
                    <h3>Vocabulary Word</h3>
                    <TextField
                        label="Word"
                        value={word.word}
                        onChange={(e) => handleVocabularyWordTextChange(index, 'word', e.target.value)}
                        required
                    />
                    <TextField
                        label="Word Pinyin"
                        value={getVocabularyWordPinyinMap(index, 'word_pinyin')}
                        onChange={(e) => handleVocabularyWordPinyinChange(index, 'word_pinyin', e.target.value)}
                        error={getVocabularyWordErrorMap(index, 'word_pinyin') != null}
                        helperText={getVocabularyWordErrorMap(index, 'word_pinyin')}
                    />
                    <Box>
                        <InputLabel htmlFor="image-upload">Prompt with Image</InputLabel>
                        <Input
                            type="file"
                            id="image-upload"
                            inputProps={{ accept: "image/*" }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleImageUpload(index, e)}
                        />
                    </Box>
                    <img
                        src={word.image ? word.image : (word.image_url ? word.image_url : undefined)}
                        alt="Uploaded"
                        style={{
                            maxHeight: '200px', maxWidth: '200px',
                            height: 'auto', width: 'auto',
                            margin: '0 auto', // Add this line to center the image horizontally
                            display: 'block', // Add this line to ensure the margin works correctly
                        }}
                    />
                    {imageError[index] && (
                        <Typography sx={{ color: 'error.main', fontSize: '0.9rem' }}>
                            Image is required.
                        </Typography>
                    )}
                    <TextField
                        label="Teacher English Prompt"
                        value={word.teacher_english_prompt}
                        onChange={(e) => handleVocabularyWordTextChange(index, 'teacher_english_prompt', e.target.value)}
                    />
                    <TextField
                        label="Teacher Chinese Prompt"
                        value={word.teacher_chinese_prompt}
                        onChange={(e) =>
                            handleVocabularyWordTextChange(index, 'teacher_chinese_prompt', e.target.value)
                        }
                    />
                    <TextField
                        label="Teacher Chinese Prompt Pinyin"
                        value={getVocabularyWordPinyinMap(index, 'teacher_chinese_prompt_pinyin')}
                        onChange={(e) => handleVocabularyWordPinyinChange(index, 'teacher_chinese_prompt_pinyin', e.target.value)}
                        error={getVocabularyWordErrorMap(index, 'teacher_chinese_prompt_pinyin') != null}
                        helperText={getVocabularyWordErrorMap(index, 'teacher_chinese_prompt_pinyin')}
                    />
                    <div>
                        <TextField
                            label="Student Chinese Response Prompt"
                            value={word.student_response_prompt ? word.student_response_prompt : "<Answer/>"}
                            onChange={(e) =>
                                handleVocabularyWordTextChange(index, 'student_response_prompt', e.target.value)
                            }
                            required
                            sx={{ width: '100%' }}
                        />
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            Use &lt;Answer/&gt; to mark where the response is inserted.<br />
                            The prompt will be skipped and not displayed, if this field is only &lt;Answer/&gt;
                        </Typography>
                    </div>
                    <TextField
                        label="Student Response Prompt Pinyin"
                        value={getVocabularyWordPinyinMap(index, 'student_response_prompt_pinyin')}
                        onChange={(e) => handleVocabularyWordPinyinChange(index, 'student_response_prompt_pinyin', e.target.value)}
                        error={getVocabularyWordErrorMap(index, 'student_response_prompt_pinyin') != null}
                        helperText={getVocabularyWordErrorMap(index, 'student_response_prompt_pinyin')}
                    />
                    <Button variant="outlined" onClick={() => handleDeleteVocabularyWord(index)}>
                        Delete
                    </Button>
                </Stack>
            )
        })}
        <Button variant="outlined" onClick={handleAddVocabularyWord}>
            Add Vocabulary Word
        </Button>
        <Dialog open={deleteConfirmationOpen} onClose={handleCancelDelete}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
                Are you sure you want to delete this vocabulary word {wordIndexToDelete !== null && (<>{vocabularyWordList[wordIndexToDelete].word}</>)} ?
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelDelete} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleConfirmDelete} color="primary">
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    </>);
};
